import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowUpRight } from 'lucide-react'
import EditorialHero from '@/components/editorial/EditorialHero'
import SectionHeader from '@/components/editorial/SectionHeader'
import Reveal from '@/components/preview/Reveal'
import { getTagsPageData } from '@/lib/postServerFns'

export const Route = createFileRoute('/blog/tag/')({
  loader: () => getTagsPageData(),
  component: TagsPage,
  head: ({ loaderData }) => {
    const tagsUrl = 'https://www.suriyaprakash.in/blog/tag'
    const title = 'All Tags | Blog | Suriyaprakash Somu'
    const tagCounts = loaderData?.tagCounts ?? []
    const description =
      'Browse every tag on the Suriyaprakash Somu blog — full-stack development, web fundamentals, networking, security, authentication, and backend systems.'
    const aiSummary = `A taxonomy of ${tagCounts.length} engineering topics covered by Suriyaprakash Somu, with article counts for each tag. Use this index to find posts about full-stack development, web fundamentals, networking, security, authentication, and backend systems.`

    return {
      meta: [
        {
          title,
        },
        {
          name: 'title',
          content: title,
        },
        {
          name: 'description',
          content: description,
        },
        { name: 'robots', content: 'index, follow' },
        { name: 'author', content: 'Suriyaprakash Somu' },
        { name: 'geo.region', content: 'IN-TN' },
        {
          name: 'geo.placename',
          content: 'Erode, Tamil Nadu, India',
        },
        { name: 'ai-summary', content: aiSummary },
        {
          name: 'keywords',
          content: [
            'software engineering blog tags',
            'web development topics',
            'full-stack development taxonomy',
            'programming article index',
            'React and Node.js topics',
            ...tagCounts.map(({ tag }) => tag),
          ].join(', '),
        },
        {
          property: 'og:title',
          content: title,
        },
        {
          property: 'og:description',
          content: description,
        },
        { property: 'og:url', content: tagsUrl },
        { property: 'og:type', content: 'website' },
        {
          property: 'og:image',
          content: 'https://www.suriyaprakash.in/og/default.png',
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          name: 'twitter:image',
          content: 'https://www.suriyaprakash.in/og/default.png',
        },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
      ],
      links: [
        {
          rel: 'canonical',
          href: tagsUrl,
        },
        {
          rel: 'alternate',
          type: 'application/rss+xml',
          title: 'RSS',
          href: 'https://www.suriyaprakash.in/rss.xml',
        },
      ],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: title,
            description,
            url: tagsUrl,
            isPartOf: {
              '@type': 'Blog',
              name: 'Suriyaprakash Somu Blog',
              url: 'https://www.suriyaprakash.in/blog',
            },
            mainEntity: {
              '@type': 'ItemList',
              name: 'Blog tags and article counts',
              numberOfItems: tagCounts.length,
              itemListElement: tagCounts.map(({ tag, count }, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: tag,
                url: `https://www.suriyaprakash.in/blog/tag/${encodeURIComponent(tag)}`,
                description: `${count} ${count === 1 ? 'article' : 'articles'}`,
              })),
            },
          }),
        },
      ],
    }
  },
})

function TagsPage() {
  const { tagCounts, totalPosts } = Route.useLoaderData()

  return (
    <div className="min-h-screen bg-sage-100 text-ink">
      <EditorialHero
        back={{ to: '/blog', label: 'Back to Blog' }}
        eyebrow="Taxonomy / All topics"
        title="Browse articles"
        accent="by tag."
        description={`${tagCounts.length} topics across the blog. Pick a tag to see every article that covers it.`}
        meta={
          <div className="preview-mono grid grid-cols-2 gap-5 text-[9px] font-bold uppercase tracking-[0.18em] text-cream-50/65 sm:text-[10px] lg:grid-cols-1">
            <p>{tagCounts.length} topics</p>
            <p>{totalPosts} published articles</p>
          </div>
        }
      />

      <section className="bg-sage-100 py-20 sm:py-28">
        <div className="container-custom">
          <SectionHeader
            eyebrow="Topic index"
            title="Every thread,"
            accent="indexed."
            description="Find articles by technology, concept or engineering topic."
          />

          <div className="mt-12 border-b border-ink/15 sm:mt-16">
            {tagCounts.length === 0 ? (
              <Reveal>
                <div className="border-t border-ink/15 py-16">
                  <p className="font-heading text-3xl sm:text-5xl">
                    No tags have been indexed yet.
                  </p>
                </div>
              </Reveal>
            ) : (
              tagCounts.map(({ tag, count }) => (
                <Reveal key={tag}>
                  <Link
                    to="/blog/tag/$tag"
                    params={{ tag }}
                    className="group grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-5 border-t border-ink/15 py-7 sm:grid-cols-[5rem_minmax(0,1fr)_auto_auto] sm:py-9"
                  >
                    <span className="preview-mono hidden text-[10px] font-bold uppercase tracking-[0.18em] text-forest sm:block">
                      Topic
                    </span>
                    <span className="font-heading text-3xl leading-none tracking-[-0.025em] transition-colors group-hover:text-forest sm:text-5xl">
                      {tag}
                    </span>
                    <span className="preview-mono text-[9px] font-bold uppercase tracking-[0.18em] text-muted sm:text-[10px]">
                      {count} {count === 1 ? 'article' : 'articles'}
                    </span>
                    <ArrowUpRight className="h-5 w-5 text-forest transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </Link>
                </Reveal>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  )
}
